import { API_PROXY_BASE as API_BASE } from '../config/api';
// Always use website API token for loyalty (same as SyrveMenu.js)
const API_LOGIN = 'df27763ebfa044aa9ab31a11ff1ad042';

class SyrveClient {
  constructor() {
    this.token = null;
    this.tokenExpiry = null;
    this.organizationId = null;
  }

  // ================== Authentication ==================
  async authenticate() {
    console.log('\n🔐 SYRVE AUTHENTICATION (via Firebase Functions Proxy)');
    console.log('API Login:', API_LOGIN ? '***' + API_LOGIN.slice(-8) : 'NOT SET');
    console.log('API Base (Proxy):', API_BASE);

    try {
      const url = `${API_BASE}/access_token`;
      console.log('Auth URL (Proxy):', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiLogin: API_LOGIN
        })
      });

      console.log('Auth response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Auth failed with status:', response.status);
        console.error('Auth error response:', errorText);
        throw new Error(`Auth failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Auth response:', {
        token: data.token ? '***' + data.token.slice(-8) : 'MISSING',
        expiresIn: data.expiresIn,
        fullResponse: JSON.stringify(data, null, 2)
      });

      this.token = data.token;
      // Token expires in seconds, convert to milliseconds
      // Default to 1 hour (3600 seconds) if expiresIn is not provided
      const expiresInSeconds = data.expiresIn || 3600;
      this.tokenExpiry = Date.now() + (expiresInSeconds * 1000);
      console.log('Token expiry:', new Date(this.tokenExpiry).toISOString(), `(expires in ${expiresInSeconds} seconds)`);

      console.log('✅ Syrve authenticated successfully');
      return data;
    } catch (error) {
      console.error('❌ Syrve authentication error:', error.message);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  // Auto-refresh token if expired
  async ensureAuthenticated() {
    const now = Date.now();
    const shouldRefresh = !this.token || now >= this.tokenExpiry - 60000;

    console.log('🔍 Checking authentication status');
    console.log('Has token:', !!this.token);
    console.log('Token expiry:', this.tokenExpiry ? new Date(this.tokenExpiry).toISOString() : 'NOT SET');
    console.log('Needs refresh:', shouldRefresh);

    if (shouldRefresh) {
      console.log('⚠️ Token refresh needed');
      await this.authenticate();
    } else {
      console.log('✅ Token still valid');
    }
  }

  // ================== Organizations ==================
  async getOrganizations() {
    await this.ensureAuthenticated();
    return this.request('/organizations', 'POST', {
      returnAdditionInfo: true,
      includeDisabled: false
    });
  }

  async setOrganization(organizationId) {
    this.organizationId = organizationId;
  }

  // ================== Customer Management ==================
  async createOrUpdateCustomer(customerData) {
    await this.ensureAuthenticated();

    const payload = {
      organizationId: this.organizationId,
      ...customerData
    };

    return this.request('/loyalty/syrve/customer/create_or_update', 'POST', payload);
  }

  async getCustomerInfo(customerId) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/info', 'POST', {
      id: customerId,
      organizationId: this.organizationId
    });
  }

  async getCustomerByPhone(phone) {
    await this.ensureAuthenticated();

    if (!phone || phone.trim() === '') {
      throw new Error('Phone number is required');
    }

    const payload = {
      phone: phone.trim().replace(/^\+/, ''),
      organizationId: this.organizationId
    };

    return this.request('/loyalty/syrve/customer/info', 'POST', payload);
  }

  // ================== Loyalty Cards ==================
  async addCardToCustomer(customerId, cardNumber, cardTrack) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/card/add', 'POST', {
      customerId,
      cardNumber,
      cardTrack: cardTrack || cardNumber, // Use cardNumber as track if not provided
      organizationId: this.organizationId
    });
  }

  async removeCardFromCustomer(customerId, cardNumber) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/card/remove', 'POST', {
      customerId,
      cardNumber,
      organizationId: this.organizationId
    });
  }

  // ================== Wallet Operations ==================
  async getWalletBalance(customerId) {
    const customerInfo = await this.getCustomerInfo(customerId);
    return customerInfo.walletBalances || [];
  }

  async addPoints(customerId, walletId, points, comment) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/wallet/topup', 'POST', {
      customerId,
      walletId,
      sum: points,
      comment: comment || `Added ${points} points`,
      organizationId: this.organizationId
    });
  }

  async usePoints(customerId, walletId, points, comment) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/wallet/chargeoff', 'POST', {
      customerId,
      walletId,
      sum: points,
      comment: comment || `Used ${points} points`,
      organizationId: this.organizationId
    });
  }

  async holdPoints(customerId, walletId, points, comment) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/wallet/hold', 'POST', {
      customerId,
      walletId,
      sum: points,
      comment: comment || `Hold ${points} points`,
      organizationId: this.organizationId
    });
  }

  async cancelHoldPoints(customerId, walletId, transactionId) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/wallet/cancel_hold', 'POST', {
      customerId,
      walletId,
      transactionId,
      organizationId: this.organizationId
    });
  }

  // ================== Loyalty Programs ==================
  async getLoyaltyPrograms() {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/program', 'POST', {
      organizationId: this.organizationId
    });
  }

  async addCustomerToProgram(customerId, programId) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/program/add', 'POST', {
      customerId,
      programId,
      organizationId: this.organizationId
    });
  }

  // ================== Order Integration ==================
  async addCustomerToOrder(orderId, customerId) {
    await this.ensureAuthenticated();

    return this.request('/order/add_customer', 'POST', {
      organizationId: this.organizationId,
      orderId,
      customer: {
        id: customerId
      }
    });
  }

  // ================== Transactions History ==================
  async getCustomerTransactions(customerId, dateFrom, dateTo) {
    await this.ensureAuthenticated();

    return this.request('/loyalty/syrve/customer/transactions/by_date', 'POST', {
      customerId,
      dateFrom: dateFrom || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Last 30 days
      dateTo: dateTo || new Date().toISOString(),
      organizationId: this.organizationId
    });
  }

  // ================== Base Request Method ==================
  async request(endpoint, method, body = null) {
    await this.ensureAuthenticated();

    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
        'Timeout': '30'
      }
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    console.log(`\n🌐 SYRVE API REQUEST to ${endpoint}`);
    console.log('Method:', method);
    console.log('Headers:', { ...options.headers, Authorization: 'Bearer ***' });
    console.log('Body:', body ? JSON.stringify(body, null, 2) : 'none');

    try {
      const url = `${API_BASE}${endpoint}`;
      console.log('Full URL:', url);

      const response = await fetch(url, options);
      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        console.error('❌ API request failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response body:', errorText);

        let error;
        try {
          error = JSON.parse(errorText);
        } catch (e) {
          error = { message: errorText || 'Unknown error' };
        }

        const errorMessage = error.errorDescription || error.message || `Request failed: ${response.status}`;
        console.error('Parsed error:', error);
        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      console.log('Response body (raw):', responseText);

      const data = responseText ? JSON.parse(responseText) : {};
      console.log('✅ API request successful');
      console.log('Response data:', JSON.stringify(data, null, 2));
      return data;
    } catch (error) {
      console.error(`\n❌ SYRVE API ERROR on ${endpoint}`);
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }
}

// Singleton instance
let syrveClientInstance = null;

export function getSyrveClient() {
  if (!syrveClientInstance) {
    syrveClientInstance = new SyrveClient();
    // Don't load from localStorage - we'll fetch fresh from API in initializeSyrve
  }
  return syrveClientInstance;
}

// Helper functions for common operations
export async function initializeSyrve(forceRefresh = false) {
  const client = getSyrveClient();
  await client.authenticate();

  // Always fetch organizations to ensure we have the correct SFA-Delivery org
  // or if forceRefresh is true
  if (!client.organizationId || forceRefresh) {
    const orgs = await client.getOrganizations();
    console.log('Available organizations:', JSON.stringify(orgs.organizations?.map(o => ({
      id: o.id,
      name: o.name,
      isDisabled: o.isDisabled
    })), null, 2));

    if (orgs && orgs.organizations && orgs.organizations.length > 0) {
      // First, try to find "SFA - Delivery" organization
      let selectedOrg = orgs.organizations.find(org =>
        !org.isDisabled && org.name?.toLowerCase().includes('sfa') && org.name?.toLowerCase().includes('delivery')
      );

      // If SFA-Delivery not found, use first active organization
      if (!selectedOrg) {
        selectedOrg = orgs.organizations.find(org => !org.isDisabled) || orgs.organizations[0];
        console.warn('⚠️ SFA-Delivery organization not found, using:', selectedOrg.name);
      } else {
        console.log('✅ Found SFA-Delivery organization:', selectedOrg.name);
      }

      await client.setOrganization(selectedOrg.id);
      console.log('Syrve initialized with organization:', selectedOrg.name, '(ID:', selectedOrg.id + ')');
    }
  } else {
    console.log('Using cached organization ID:', client.organizationId);
  }

  return client;
}

export default getSyrveClient;